import { useEffect, useState } from "react";
import { Copy, RefreshCw, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { streamSSE } from "@/lib/api";

export const TOOLS = [
  { id: "google_flow", label: "Google Flow", kind: "Video · Veo" },
  { id: "gemini", label: "Gemini", kind: "Immagine" },
  { id: "nano_banana", label: "Nano Banana", kind: "Immagine · Edit" },
  { id: "canva", label: "Canva", kind: "Magic Media" },
  { id: "midjourney", label: "Midjourney", kind: "Immagine" },
  { id: "stable_diffusion", label: "Stable Diffusion", kind: "+ Negative" },
  { id: "flux", label: "FLUX", kind: "Immagine" },
  { id: "chatgpt", label: "ChatGPT", kind: "GPT Image" },
  { id: "sora", label: "Sora", kind: "Video" },
  { id: "runway", label: "Runway", kind: "Video" },
];

export const ToolPrompts = ({ analysisId, saved }) => {
  const [variants, setVariants] = useState(saved || {});
  const [tool, setTool] = useState("google_flow");
  const [busy, setBusy] = useState(false);
  useEffect(() => setVariants(saved || {}), [analysisId, saved]);

  const generate = async (id) => {
    const prev = variants[id];
    let ok = false;
    setTool(id);
    setBusy(true);
    setVariants((v) => ({ ...v, [id]: "" }));
    try {
      await streamSSE(`/analyses/${analysisId}/variants/${id}`, (ev) => {
        if (ev.type === "delta") setVariants((v) => ({ ...v, [id]: (v[id] || "") + ev.content }));
        if (ev.type === "done") { ok = true; setVariants((v) => ({ ...v, [id]: ev.prompt })); }
        if (ev.type === "error") toast.error(ev.message);
      });
    } catch (e) {
      toast.error(e.message);
    } finally {
      if (!ok) setVariants((v) => ({ ...v, [id]: prev }));
      setBusy(false);
    }
  };

  const select = (id) => { if (busy) return; variants[id] ? setTool(id) : generate(id); };
  const text = variants[tool];
  const label = TOOLS.find((t) => t.id === tool)?.label;

  return (
    <section className="py-8 border-b rf-line rf-fade" data-testid="tool-prompts-section">
      <div className="flex items-baseline gap-4 mb-5">
        <Sparkles size={16} className="rf-red self-center" />
        <h3 className="font-bold">Prompt per un'IA specifica</h3>
        <span className="rf-mono text-[10px] tracking-[0.15em] uppercase text-[var(--rf-grey)]">Scegli il tool</span>
      </div>
      <div className="flex flex-wrap gap-2" data-testid="tool-chips">
        {TOOLS.map((t) => (
          <button key={t.id} onClick={() => select(t.id)} disabled={busy && t.id !== tool}
            className={`px-3 py-2 border text-left transition-colors duration-150 disabled:opacity-40 ${tool === t.id && text !== undefined ? "bg-[var(--rf-ink)] text-white border-[var(--rf-ink)]" : "border-[var(--rf-line)] hover:border-[var(--rf-ink)]"}`}
            data-testid={`tool-chip-${t.id}`}>
            <span className="block text-sm font-semibold leading-tight">{t.label}{variants[t.id] ? " ✓" : ""}</span>
            <span className="block rf-mono text-[9px] tracking-wider uppercase opacity-60">{t.kind}</span>
          </button>
        ))}
      </div>
      {text !== undefined && (
        <div className="mt-6 space-y-4" data-testid="tool-prompt-output">
          <p className={`rf-mono text-sm leading-relaxed whitespace-pre-wrap bg-[var(--rf-soft)] p-5 ${busy ? "rf-caret" : ""}`} data-testid="tool-prompt-text">{text}</p>
          {!busy && text && (
            <div className="flex flex-wrap gap-3">
              <button onClick={async () => { await navigator.clipboard.writeText(text); toast.success(`Prompt ${label} copiato`); }}
                className="rf-btn rf-btn-sm" data-testid="tool-prompt-copy-button"><Copy size={13} /> Copia per {label}</button>
              <button onClick={() => generate(tool)} className="rf-btn rf-btn-ghost rf-btn-sm" data-testid="tool-prompt-regenerate-button">
                <RefreshCw size={13} /> Rigenera
              </button>
            </div>
          )}
        </div>
      )}
    </section>
  );
};
